import { ReactNode, useContext, useEffect, useRef, useState } from 'react';

import poolSuccessImgPath from 'assets/pngs/carve-confirmation.png';
import { PillFilters } from 'features/common-elements';
import { LoanTable } from 'features/drilldown/LoanDatatable/LoanTable';
import { InProgressOverlay } from 'features/loanTape/InProgressOverlay';
import LatestUploadBanner from 'features/loanTape/LatestUploadBanner';
import { useTourContext } from 'tours/TourProvider';
import { TOUR_CONFIGS } from 'tours/config';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Dialog,
  Button,
  SelectionStatus,
  SelectionStatusLine,
  useDialog,
  DialogActions,
  Dropdown,
  ModalNotStyled,
} from 'ui-kit';

import CreatePoolForm, { FormValues } from './CreatePoolForm';
import { usePoolLoans } from './gql/getPoolLoans_gql';
import { useCreatePool } from './hooks/useCreatePool';
import { useLoanLevelData } from './hooks/useLoanLevelData';
import { usePoolOptions } from './hooks/usePoolOptions';
import { usePoolSelection } from './hooks/usePoolSelection';
import { usePortfolioLoans } from './hooks/usePortfolioLoans';
import { useSelection } from './hooks/useSelection';
import { CreDetailsModal } from './Portfolio/CreDetailsModal';
import { PortfolioLayout } from './Portfolio/PortfolioLayout';
import { useSyncSelectedPoolsData } from './hooks/useSyncSelectedPoolsData';
import { AssetClass } from '../../../__generated__/globalTypes';
import { DataContext, DataContextActionType } from '../../../context/DataContext';
import { useFeatureFlag } from '../../../context/FeatureFlagContext';
import { useColumnOrder } from '../../../hooks/useColumnOrder';

import { Pool } from 'query/getPools';
/**
 * Reusable dialog component for pool creation success/started states.
 * Extracts common styling patterns used across pool dialogs.
 */
interface PoolSuccessDialogProps {
  dialog: ReturnType<typeof useDialog>;
  title: string;
  message: string;
  actions: ReactNode;
  imageAlt?: string;
}

function PoolSuccessDialog({
  dialog,
  title,
  message,
  actions,
  imageAlt = 'pool success',
}: PoolSuccessDialogProps) {
  return (
    <Dialog dialog={dialog}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <img src={poolSuccessImgPath} className="z-10 h-[232px]" alt={imageAlt} />
        <div className="mb-2 mt-[19px] text-center text-[24px] font-medium leading-[36px] text-white">
          {title}
        </div>
        <div className="mb-[32px] w-[300px] text-[16px] leading-[24px] tracking-[0px] text-white">
          {message}
        </div>
        {actions}
      </div>
    </Dialog>
  );
}

//temporary while not assetClasses are available
export const getLoanTableConfigName = (assetsClass: AssetClass | 'HOME_ARM' | null) => {
  switch (assetsClass) {
    case AssetClass.HOME:
      return 'HOME';
    case AssetClass.AUTO:
      return 'AUTO';
    case AssetClass.UNSECPERSONAL:
      return 'UNSECPERSONAL';
    case 'HOME_ARM':
      return 'HOME_ARM';
    case AssetClass.CRE:
      return AssetClass.CRE;
    case AssetClass.HELOC:
      return AssetClass.HELOC;
    case AssetClass.MARINE:
      return AssetClass.MARINE;
    default:
      // Falling through to HOME aliases the unknown class onto HOME's saved
      // column order — both tables then read AND WRITE the same storage key.
      // Any class rendered by LoanTable needs its own case here.
      return 'HOME';
  }
};

const TOUR_START_DELAY_MS = 300;
const MAX_RETRY_ATTEMPTS = 20;
const RETRY_DELAY_MS = 150;

const LoanLevelData = () => {
  const { state, dispatch } = useContext(DataContext);
  const { startTour, tourDataLoading } = useTourContext();
  const hasStartedTours = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTourRef = useRef(startTour);
  const retryAttemptsRef = useRef(0);

  const {
    gqlAssetClass,
    filterProps,
    navigateToPoolManager,
    queryFilters,
    loansSorting,
    setLoansSorting,
    assetClass,
  } = useLoanLevelData();

  // Pool selection state
  const [selectedPool, setSelectedPool] = useState<{
    label: string;
    value: string;
    poolData: Pool;
  } | null>(null);

  // Get pool URL parameter
  const { setPool } = filterProps;

  const { poolOptions } = usePoolOptions({
    assetClass: assetClass,
  });

  // Portfolio context for summary display
  useSyncSelectedPoolsData(selectedPool ? [selectedPool.poolData] : []);
  const poolCreatedDialog = useDialog();
  const poolCreationStartedDialog = useDialog();
  const errorDialog = useDialog();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { createPool, lastCreatedPool, pendingPoolJob, createPoolLoading } = useCreatePool();
  const isCreEnabled = useFeatureFlag('creEnabled');

  const navigate = useNavigate();
  let location = useLocation();

  // Use pool loans query when pool is selected, otherwise use company loans query
  const {
    data: poolData,
    loading: poolLoading,
    previousData: poolPreviousData,
    params: poolParams,
    updateParams: poolUpdateParams,
  } = usePoolLoans(selectedPool?.value || null, queryFilters, loansSorting);

  const {
    data: companyData,
    loading: companyLoading,
    previousData: companyPreviousData,
    params: companyParams,
    updateParams: companyUpdateParams,
  } = usePortfolioLoans(gqlAssetClass, queryFilters, loansSorting);

  // Use pool data if pool is selected, otherwise use company data
  const data = selectedPool ? poolData : companyData;
  const loading = selectedPool ? poolLoading : companyLoading;
  const previousData = selectedPool ? poolPreviousData : companyPreviousData;
  const params = selectedPool ? poolParams : companyParams;
  const updateParams = selectedPool ? poolUpdateParams : companyUpdateParams;

  const [creModalLoanId, setCreModalLoanId] = useState<string>('');
  const [isOpenCREModal, setIsOpenCREModal] = useState<boolean>(false);

  const handleCREClick = (loanId: string) => {
    setIsOpenCREModal(true);
    setCreModalLoanId(loanId);
    navigate(`${location.pathname}/${loanId}`);
  };

  const handleCloseCREModal = () => {
    setIsOpenCREModal(false);
    navigate('/portfolio/loan-level-data/cre');
  };

  useEffect(() => {
    if (location.pathname.includes('/cre/') && !isOpenCREModal) {
      setIsOpenCREModal(true);
      setCreModalLoanId(location.pathname.split('cre/')[1]);
    }
  }, [location.pathname]);

  useEffect(() => {
    dispatch({
      type: DataContextActionType.SET_LOADING,
      payload: loading,
    });
  }, [loading]);

  useEffect(() => {
    startTourRef.current = startTour;
  }, [startTour]);

  usePoolSelection({
    setSelectedPool,
  });

  const { setColumnOrder } = useColumnOrder(
    `loanTable_${getLoanTableConfigName(gqlAssetClass)}`,
    state.globalTableOrderConfig?.myPortfolio?.loanTable[
      getLoanTableConfigName(gqlAssetClass)
    ] as string[],
  );

  const {
    selectedLoans,
    setSelectedLoans,
    selectionStatus,
    setSelectionStatus,
    onClear,
    onSelectPage,
    onSelectAll,
  } = useSelection(params, data);

  // Auto-start tour on page load (wait for data to be ready)
  useEffect(() => {
    if (hasStartedTours.current) {
      return;
    }

    if (tourDataLoading || !gqlAssetClass || !filterProps.filtersConfig || loading) {
      return;
    }

    const checkTourElementsReady = (): boolean => {
      const firstElement = document.querySelector(
        '[data-tour="stratifications-portfolio-summary"]',
      );
      return firstElement !== null;
    };

    const attemptStartTour = () => {
      if (hasStartedTours.current || tourDataLoading || loading) {
        return;
      }

      if (checkTourElementsReady()) {
        retryAttemptsRef.current = 0;
        timeoutRef.current = setTimeout(() => {
          if (hasStartedTours.current || tourDataLoading || loading) {
            return;
          }
          hasStartedTours.current = true;

          // Get base steps from config
          const baseSteps = TOUR_CONFIGS['portfolio.loan-level-data']?.steps || [];

          startTourRef.current('portfolio.loan-level-data', { steps: baseSteps });
          timeoutRef.current = null;
        }, TOUR_START_DELAY_MS);
      } else if (retryAttemptsRef.current < MAX_RETRY_ATTEMPTS) {
        retryAttemptsRef.current += 1;
        timeoutRef.current = setTimeout(attemptStartTour, RETRY_DELAY_MS);
      } else {
        console.warn('[Tour] Loan Level Data tour elements not found after max retries');
      }
    };

    attemptStartTour();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [tourDataLoading, gqlAssetClass, filterProps.filtersConfig, loading]);

  // Handle SelectionStatusLine tour trigger when it becomes visible
  const hasStartedSelectionStatusLineTour = useRef(false);
  const selectionStatusLineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if SelectionStatusLine is visible (has content)
    const checkSelectionStatusLineVisible = (): boolean => {
      const element = selectionStatusLineRef.current;
      if (!element) return false;
      const statusLineDiv = element.querySelector('.bg-blue-600');
      return statusLineDiv !== null;
    };

    const isVisible = checkSelectionStatusLineVisible();

    // Reset flag when SelectionStatusLine becomes invisible
    if (!isVisible) {
      hasStartedSelectionStatusLineTour.current = false;
      return;
    }

    if (
      hasStartedSelectionStatusLineTour.current ||
      tourDataLoading ||
      !gqlAssetClass ||
      !filterProps.filtersConfig ||
      loading
    ) {
      return;
    }

    // Only start tour if SelectionStatusLine is visible
    const timer = setTimeout(() => {
      if (hasStartedSelectionStatusLineTour.current || !checkSelectionStatusLineVisible()) {
        return;
      }
      hasStartedSelectionStatusLineTour.current = true;
      startTour('portfolio.loan-level-data.selection-status-line');
    }, 500);

    return () => clearTimeout(timer);
  }, [
    tourDataLoading,
    gqlAssetClass,
    filterProps.filtersConfig,
    loading,
    selectionStatus,
    selectedLoans,
    startTour,
  ]);

  if (!gqlAssetClass || !filterProps.filtersConfig) {
    return <PortfolioLayout />;
  }

  const dataToShow = data || previousData;

  // Extract data based on whether we're showing pool or company data
  const loans = selectedPool
    ? (dataToShow as any)?.user?.company?.pool?.loans
    : (dataToShow as any)?.user?.company?.loans;

  const performanceData = selectedPool
    ? (dataToShow as any)?.user?.company?.pool?.performance_summary
    : (dataToShow as any)?.user?.company?.performanceData;

  // @ts-ignore
  const loanCount = performanceData?.loanCount || 0;
  // In v1 mode, totalLoans represents the filtered loan count
  const totalLoans = loanCount;

  const potentialPoolSize =
    selectionStatus === SelectionStatus.AllSelected
      ? totalLoans
      : Object.keys(selectedLoans).length;

  // Auto-start create pool button tour when loans are selected
  const hasStartedCreatePoolTour = useRef(false);

  useEffect(() => {
    // Reset when no loans are selected
    if (potentialPoolSize === 0) {
      hasStartedCreatePoolTour.current = false;
      return;
    }

    if (
      hasStartedCreatePoolTour.current ||
      tourDataLoading ||
      !gqlAssetClass ||
      !filterProps.filtersConfig ||
      loading ||
      potentialPoolSize === 0
    ) {
      return;
    }

    // Start tour when loans are selected (button is always in DOM)
    const timer = setTimeout(() => {
      if (hasStartedCreatePoolTour.current) {
        return;
      }
      hasStartedCreatePoolTour.current = true;
      startTour('portfolio.loan-level-data.create-pool-button');
    }, 300);

    return () => clearTimeout(timer);
  }, [
    tourDataLoading,
    gqlAssetClass,
    filterProps.filtersConfig,
    loading,
    potentialPoolSize,
    startTour,
  ]);

  const onSubmitCreatePoolForm = async (formValues: FormValues) => {
    try {
      const result = await createPool(
        formValues,
        gqlAssetClass,
        queryFilters,
        selectedLoans,
        selectionStatus,
      );

      if (result.type === 'sync') {
        // Pool created synchronously - show success dialog
        poolCreatedDialog.openDialog();
      } else {
        // Pool creation started as async job - show job started dialog
        poolCreationStartedDialog.openDialog();
      }
    } catch (error) {
      const errorMessageText =
        error instanceof Error ? error.message : 'Unknown error while creating pool';

      toast.error(`Error creating pool: "${errorMessageText}".`);
      setErrorMessage(
        'There was an error creating the loan pool. Please retry or contact support.',
      );
      errorDialog.openDialog();
      throw error;
    }
  };

  const handlePoolChange = (option: { label: string; value: string; poolData?: Pool } | null) => {
    setSelectedPool(
      option as {
        label: string;
        value: string;
        poolData: Pool;
      },
    );
    setPool(option?.value || null);
  };

  //if need to select loans which are not in a pool
  // const filterSelectableLoans = (
  //   newSelection: Updater<RowSelectionState>,
  //   loans: GetDrilldown_user_company_loans[],
  // ): Updater<RowSelectionState> => {
  //   return Object.fromEntries(
  //     Object.entries(newSelection).filter(([key]) => {
  //       const loanId = key.split(':')[1];
  //       const loan = loans.find((loan) => loan.id === loanId);
  //       return !loan?.listing;
  //     }),
  //   );
  // };

  return (
    <>
      <InProgressOverlay
        title="Creating Pool In Progress"
        description={`Creating pool of ${potentialPoolSize.toLocaleString()} loans`}
        open={createPoolLoading}
        hideButton={true}
      />

      {isOpenCREModal ? (
        <CreDetailsModal
          handleCloseCREModal={handleCloseCREModal}
          creModalLoanId={creModalLoanId}
          loansData={loans}
          role="SELLER"
        />
      ) : (
        <PortfolioLayout
          filterRow={
            <div className="flex w-full items-center justify-between gap-4">
              <div className="flex-1" data-tour="loan-level-data-pill-filters">
                <PillFilters {...filterProps} filtersConfig={filterProps.filtersConfig} />
              </div>
              <div className="flex items-center gap-2" data-tour="loan-level-data-pool-selector">
                <span className="text-sm text-gray-600">Pool:</span>
                <Dropdown
                  options={poolOptions || []}
                  value={selectedPool?.label}
                  className="w-48 border-none"
                  placeholder="Select Pool"
                  onValueChange={(value: any) => {
                    handlePoolChange(value);
                  }}
                  onClear={() => handlePoolChange(null)}
                  showClear={true}
                />
              </div>
            </div>
          }
          actions={
            <div className="ml-4 flex items-center gap-2">
              <ModalNotStyled
                trigger={
                  <div data-tour="loan-level-data-create-pool-button">
                    <Button
                      type="primary_dark"
                      disabled={potentialPoolSize === 0}
                      label="select loans"
                      className="w-max"
                    >
                      {potentialPoolSize === 0
                        ? 'Select loans to create a pool'
                        : `Create pool of ${potentialPoolSize.toLocaleString()} loans`}
                    </Button>
                  </div>
                }
              >
                {({ closeModal }) => (
                  <CreatePoolForm closeModal={closeModal} createPool={onSubmitCreatePoolForm} />
                )}
              </ModalNotStyled>
            </div>
          }
        >
          {loans ? (
            <>
              <LatestUploadBanner />
              <div ref={selectionStatusLineRef} data-tour="loan-level-data-selection-status-line">
                <SelectionStatusLine
                  status={selectionStatus}
                  numberSelectedOnPage={Object.keys(selectedLoans).length}
                  totalItems={totalLoans}
                  onClear={onClear}
                  onSelectAll={onSelectAll}
                />
              </div>
              <LoanTable
                //@ts-ignore
                data={loans ?? []}
                assetClass={gqlAssetClass}
                strings={{
                  empty: selectedPool ? 'No loans in this pool' : 'No Loans Found',
                }}
                selection={{
                  selected: selectedLoans,
                  onSelectionChange: (newSelection) => {
                    //if need to select loans which are not in a pool
                    // const filteredSelection = filterSelectableLoans(
                    //   newSelection,
                    //   loans,
                    // );

                    setSelectedLoans(newSelection);
                    setSelectionStatus(SelectionStatus.None);
                  },
                  onHeaderCheckboxChange: (checked) => {
                    if (checked) {
                      onSelectPage();
                    } else {
                      onClear();
                    }
                  },
                }}
                sorting={{
                  state: loansSorting,
                  onSortingChanged: setLoansSorting,
                }}
                pagination={{
                  updateParams: updateParams,
                  queryParams: params,
                  totalItems: loanCount,
                }}
                setColumnOrder={setColumnOrder}
                handleCREClick={
                  isCreEnabled && gqlAssetClass === AssetClass.CRE ? handleCREClick : undefined
                }
              />
            </>
          ) : (
            <div className="flex h-64 items-center justify-center text-gray-500">
              {!selectedPool ? 'Select a pool to view loans' : 'No data available'}
            </div>
          )}

          {/* For debugging, show list of selected account ids */}
          {/* <div>Selected Loans:</div>
      <div>{JSON.stringify(selectedLoans)}</div> */}

          <PoolSuccessDialog
            dialog={poolCreatedDialog}
            title="Pool Created"
            message={`"${lastCreatedPool?.friendlyName}" is now available. Would you like to go to the Pool Manager?`}
            imageAlt="pool created"
            actions={
              <div className="flex w-[150px] justify-between gap-[30px]">
                <Button
                  size="medium"
                  type="secondary"
                  label="no"
                  onClick={() => poolCreatedDialog.closeDialog()}
                >
                  No
                </Button>
                <Button
                  size="medium"
                  label="yes"
                  onClick={() => {
                    poolCreatedDialog.closeDialog();
                    navigateToPoolManager();
                  }}
                >
                  Yes
                </Button>
              </div>
            }
          />

          <PoolSuccessDialog
            dialog={poolCreationStartedDialog}
            title="Pool Creation Started"
            message={`"${pendingPoolJob?.friendlyName}" is being created. This may take a few minutes for large pools. You can track progress in the Jobs dropdown in the header.`}
            imageAlt="pool creation started"
            actions={
              <div className="flex justify-center">
                <Button
                  size="medium"
                  label="ok"
                  onClick={() => poolCreationStartedDialog.closeDialog()}
                >
                  OK
                </Button>
              </div>
            }
          />

          <Dialog dialog={errorDialog}>
            <>
              <div>{errorMessage}</div>
              <DialogActions>
                <Button size="medium" label="OK" onClick={() => errorDialog.closeDialog()}>
                  OK
                </Button>
              </DialogActions>
            </>
          </Dialog>
        </PortfolioLayout>
      )}
    </>
  );
};

export default LoanLevelData;
